import { motion } from "framer-motion";
import { 
  Calendar, Clock, DollarSign, TrendingUp, TrendingDown, 
  CheckCircle, Circle, Tag, Repeat, AlertTriangle,
  ListTodo, Bell, Wallet, Target, FileText, Sparkles
} from "lucide-react";
import { format, parseISO, isToday, isTomorrow, isPast } from "date-fns";
import { ptBR } from "date-fns/locale";

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('pt-BR', { 
    style: 'currency', 
    currency: 'BRL' 
  }).format(amount);
};

// Format date nicely
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
    if (isToday(date)) return "Hoje";
    if (isTomorrow(date)) return "Amanhã";
    return format(date, "dd MMM", { locale: ptBR });
  } catch {
    return dateStr;
  }
};

// Category colors
const categoryColors = {
  alimentação: { bg: "bg-orange-500/20", text: "text-orange-400", icon: "🍕" },
  alimentacao: { bg: "bg-orange-500/20", text: "text-orange-400", icon: "🍕" },
  transporte: { bg: "bg-blue-500/20", text: "text-blue-400", icon: "🚗" },
  lazer: { bg: "bg-purple-500/20", text: "text-purple-400", icon: "🎬" },
  saúde: { bg: "bg-green-500/20", text: "text-green-400", icon: "💊" },
  saude: { bg: "bg-green-500/20", text: "text-green-400", icon: "💊" },
  educação: { bg: "bg-cyan-500/20", text: "text-cyan-400", icon: "📚" },
  educacao: { bg: "bg-cyan-500/20", text: "text-cyan-400", icon: "📚" },
  moradia: { bg: "bg-amber-500/20", text: "text-amber-400", icon: "🏠" },
  contas: { bg: "bg-red-500/20", text: "text-red-400", icon: "📄" },
  trabalho: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: "💼" },
  salário: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: "💰" },
  salario: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: "💰" },
  freelance: { bg: "bg-violet-500/20", text: "text-violet-400", icon: "💻" },
  investimentos: { bg: "bg-yellow-500/20", text: "text-yellow-400", icon: "📈" },
  outros: { bg: "bg-slate-500/20", text: "text-slate-400", icon: "📦" },
  default: { bg: "bg-slate-500/20", text: "text-slate-400", icon: "📦" }
};

// Get category style
const getCategoryStyle = (category) => {
  const key = category?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return categoryColors[key] || categoryColors.default;
};

// Transaction Card Component
export const TransactionCard = ({ transaction, index = 0 }) => {
  const isExpense = transaction.type === "expense";
  const categoryStyle = getCategoryStyle(transaction.category);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`flex items-center justify-between p-3 rounded-lg border 
        ${isExpense ? 'bg-red-500/5 border-red-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg
          ${categoryStyle.bg}`}>
          {categoryStyle.icon}
        </div>
        <div>
          <p className="text-white font-medium text-sm">
            {transaction.description || transaction.category || "Transação"}
          </p>
          <div className="flex items-center gap-2 text-xs text-white/50">
            <span className={`px-1.5 py-0.5 rounded ${categoryStyle.bg} ${categoryStyle.text}`}>
              {transaction.category || "outros"}
            </span>
            <span>•</span>
            <span>{formatDate(transaction.date)}</span>
          </div>
        </div>
      </div>
      <div className={`font-semibold ${isExpense ? 'text-red-400' : 'text-emerald-400'}`}>
        {isExpense ? '-' : '+'} {formatCurrency(transaction.amount)}
      </div>
    </motion.div>
  );
};

// Transactions List Card (multiple transactions)
export const TransactionsListCard = ({ transactions, title = "Transações", period = "" }) => {
  const expenses = transactions.filter(t => t.type === "expense");
  const incomes = transactions.filter(t => t.type === "income");
  const totalExpenses = expenses.reduce((sum, t) => sum + t.amount, 0);
  const totalIncome = incomes.reduce((sum, t) => sum + t.amount, 0);
  const balance = totalIncome - totalExpenses;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden max-w-md"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600/20 to-purple-600/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-violet-400" />
          <h3 className="text-white font-semibold">{title}</h3>
          {period && <span className="text-white/50 text-sm">• {period}</span>}
        </div>
      </div>
      
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 p-3 bg-slate-900/30">
        <div className="text-center">
          <p className="text-xs text-white/50">Receitas</p>
          <p className="text-emerald-400 font-semibold text-sm">{formatCurrency(totalIncome)}</p>
        </div>
        <div className="text-center border-x border-slate-700/50">
          <p className="text-xs text-white/50">Despesas</p>
          <p className="text-red-400 font-semibold text-sm">{formatCurrency(totalExpenses)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-white/50">Saldo</p>
          <p className={`font-semibold text-sm ${balance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {formatCurrency(balance)}
          </p>
        </div>
      </div>
      
      {/* Transactions List */}
      <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
        {transactions.length === 0 ? (
          <p className="text-white/50 text-center py-4 text-sm">Nenhuma transação encontrada</p>
        ) : (
          transactions.map((t, i) => (
            <TransactionCard key={t.id || i} transaction={t} index={i} />
          ))
        )}
      </div>
      
      {/* Footer with count */}
      <div className="px-4 py-2 bg-slate-900/30 border-t border-slate-700/50 text-center">
        <span className="text-xs text-white/40">
          {transactions.length} transação{transactions.length !== 1 ? 'ões' : ''} • 
          {expenses.length} despesa{expenses.length !== 1 ? 's' : ''} • 
          {incomes.length} receita{incomes.length !== 1 ? 's' : ''}
        </span>
      </div>
    </motion.div>
  );
};

// Reminder Card Component
export const ReminderCard = ({ reminder, index = 0 }) => {
  const isPastDue = reminder.datetime && isPast(parseISO(reminder.datetime));
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`flex items-center gap-3 p-3 rounded-lg border 
        ${isPastDue ? 'bg-red-500/10 border-red-500/30' : 'bg-cyan-500/10 border-cyan-500/30'}`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center 
        ${isPastDue ? 'bg-red-500/20' : 'bg-cyan-500/20'}`}>
        <Bell className={`w-5 h-5 ${isPastDue ? 'text-red-400' : 'text-cyan-400'}`} />
      </div>
      <div className="flex-1">
        <p className="text-white font-medium text-sm">{reminder.message}</p>
        <div className="flex items-center gap-2 text-xs text-white/50">
          <Clock className="w-3 h-3" />
          <span>{formatDate(reminder.datetime)}</span>
          {reminder.datetime && (
            <span>• {format(parseISO(reminder.datetime), "HH:mm")}</span>
          )}
          {reminder.is_recurring && (
            <>
              <span>•</span>
              <Repeat className="w-3 h-3 text-purple-400" />
              <span className="text-purple-400">{reminder.recurrence}</span>
            </>
          )}
        </div>
      </div>
      {isPastDue && (
        <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
          Atrasado
        </span>
      )}
    </motion.div>
  );
};

// Reminders List Card
export const RemindersListCard = ({ reminders, title = "Lembretes" }) => {
  const upcoming = reminders.filter(r => !r.completed);
  const overdue = upcoming.filter(r => r.datetime && isPast(parseISO(r.datetime)));
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden max-w-md"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-400" />
            <h3 className="text-white font-semibold">{title}</h3>
          </div>
          {overdue.length > 0 && (
            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {overdue.length} atrasado{overdue.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      
      {/* Reminders List */}
      <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
        {reminders.length === 0 ? (
          <p className="text-white/50 text-center py-4 text-sm">Nenhum lembrete</p>
        ) : (
          reminders.map((r, i) => (
            <ReminderCard key={r.id || i} reminder={r} index={i} />
          ))
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 bg-slate-900/30 border-t border-slate-700/50 text-center">
        <span className="text-xs text-white/40">
          {upcoming.length} lembrete{upcoming.length !== 1 ? 's' : ''} pendente{upcoming.length !== 1 ? 's' : ''}
        </span>
      </div>
    </motion.div>
  );
};

// Task Card Component
export const TaskCard = ({ task, index = 0 }) => {
  const isCompleted = task.is_completed || task.status === 'done';
  const isPriorityHigh = task.priority === 'high' || task.priority === 'urgent';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`flex items-center gap-3 p-3 rounded-lg border 
        ${isCompleted ? 'bg-emerald-500/5 border-emerald-500/20' : 
          isPriorityHigh ? 'bg-red-500/5 border-red-500/20' : 'bg-slate-500/5 border-slate-500/20'}`}
    >
      <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2
        ${isCompleted ? 'border-emerald-500 bg-emerald-500/20' : 
          isPriorityHigh ? 'border-red-400' : 'border-slate-500'}`}>
        {isCompleted ? (
          <CheckCircle className="w-4 h-4 text-emerald-400" />
        ) : (
          <Circle className={`w-4 h-4 ${isPriorityHigh ? 'text-red-400' : 'text-slate-500'}`} />
        )}
      </div>
      <div className="flex-1">
        <p className={`font-medium text-sm ${isCompleted ? 'text-white/50 line-through' : 'text-white'}`}>
          {task.title}
        </p>
        {(task.datetime_due || task.priority) && (
          <div className="flex items-center gap-2 text-xs text-white/50">
            {task.datetime_due && (
              <>
                <Clock className="w-3 h-3" />
                <span>{formatDate(task.datetime_due)}</span>
              </>
            )}
            {task.priority && (
              <span className={`px-1.5 py-0.5 rounded text-xs
                ${task.priority === 'high' || task.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                  task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-500/20 text-slate-400'}`}>
                {task.priority}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Tasks List Card
export const TasksListCard = ({ tasks, title = "Tarefas" }) => {
  const pending = tasks.filter(t => !t.is_completed && t.status !== 'done');
  const completed = tasks.filter(t => t.is_completed || t.status === 'done');
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden max-w-md"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-amber-400" />
            <h3 className="text-white font-semibold">{title}</h3>
          </div>
          <span className="text-xs text-white/50">
            {completed.length}/{tasks.length} concluídas
          </span>
        </div>
      </div>
      
      {/* Tasks List */}
      <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
        {tasks.length === 0 ? (
          <p className="text-white/50 text-center py-4 text-sm">Nenhuma tarefa</p>
        ) : (
          tasks.map((t, i) => (
            <TaskCard key={t.id || i} task={t} index={i} />
          ))
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 bg-slate-900/30 border-t border-slate-700/50 text-center">
        <span className="text-xs text-white/40">
          {pending.length} pendente{pending.length !== 1 ? 's' : ''}
        </span>
      </div>
    </motion.div>
  );
};

// Finance Summary Card
export const FinanceSummaryCard = ({ summary }) => {
  const balance = (summary.total_income || 0) - (summary.total_expenses || 0);
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden max-w-md"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600/20 to-teal-600/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-400" />
          <h3 className="text-white font-semibold">Resumo Financeiro</h3>
        </div>
      </div>
      
      {/* Stats */}
      <div className="p-4 space-y-4">
        {/* Main balance */}
        <div className="text-center py-3 bg-slate-900/30 rounded-lg">
          <p className="text-xs text-white/50 mb-1">Saldo do Mês</p>
          <p className={`text-3xl font-bold ${balance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {formatCurrency(balance)}
          </p>
        </div>
        
        {/* Income & Expenses */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
            <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <p className="text-xs text-white/50">Receitas</p>
            <p className="text-emerald-400 font-semibold">{formatCurrency(summary.total_income || 0)}</p>
          </div>
          <div className="bg-red-500/10 rounded-lg p-3 text-center">
            <TrendingDown className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <p className="text-xs text-white/50">Despesas</p>
            <p className="text-red-400 font-semibold">{formatCurrency(summary.total_expenses || 0)}</p>
          </div>
        </div>
        
        {/* Top expenses by category */}
        {summary.by_category && Object.keys(summary.by_category).length > 0 && (
          <div>
            <p className="text-xs text-white/50 mb-2">Por Categoria</p>
            <div className="space-y-1">
              {Object.entries(summary.by_category).slice(0, 4).map(([cat, amount]) => {
                const style = getCategoryStyle(cat);
                return (
                  <div key={cat} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span>{style.icon}</span>
                      <span className="text-white/70 capitalize">{cat}</span>
                    </div>
                    <span className="text-red-400">{formatCurrency(amount)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Web Search Results Card Component
export const WebSearchCard = ({ results, query, title }) => {
  if (!results || results.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4 mt-4"
      data-testid="web-search-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-cyan-400" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-slate-200">
            {title || "Resultados da Pesquisa"}
          </h3>
          <p className="text-xs text-slate-400">Pesquisa: "{query}"</p>
        </div>
      </div>

      <div className="space-y-2">
        {results.slice(0, 3).map((result, index) => (
          <motion.a
            key={index}
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="block p-3 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors border border-slate-600/30"
            data-testid={`web-result-${index}`}
          >
            <div className="flex items-start gap-2">
              <span className="text-xs font-mono text-slate-500 mt-0.5">{index + 1}</span>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-cyan-400 truncate">
                  {result.title}
                </h4>
                <p className="text-xs text-slate-400 line-clamp-2 mt-1">
                  {result.snippet}
                </p>
                <p className="text-xs text-slate-500 truncate mt-1">
                  {result.url?.replace(/^https?:\/\//, '').split('/')[0]}
                </p>
              </div>
            </div>
          </motion.a>
        ))}
      </div>

      <p className="text-xs text-slate-500 mt-3 text-center">
        Clique em um resultado para saber mais
      </p>
    </motion.div>
  );
};

// Calendar Events Card
export const CalendarEventsCard = ({ events, date, title = "Agenda" }) => {
  if (!events || events.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-3 p-4 bg-nectar-bg border border-nectar-border rounded-xl"
      >
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-brand-cyan" />
          <span className="text-sm font-medium text-white">{title}</span>
          {date && <span className="text-xs text-slate-500">• {formatDate(date)}</span>}
        </div>
        <p className="text-sm text-slate-400">Nenhum evento encontrado.</p>
      </motion.div>
    );
  }

  // Format event time
  const formatEventTime = (dateStr, isAllDay) => {
    if (isAllDay) return "Dia todo";
    try {
      const date = parseISO(dateStr);
      return format(date, "HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-3 p-4 bg-nectar-bg border border-nectar-border rounded-xl"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-brand-cyan" />
          <span className="text-sm font-medium text-white">{title}</span>
          {date && <span className="text-xs text-slate-500">• {formatDate(date)}</span>}
        </div>
        <span className="text-xs text-slate-500">{events.length} evento(s)</span>
      </div>

      <div className="space-y-2 max-h-60 overflow-y-auto">
        {events.slice(0, 10).map((event, idx) => (
          <motion.div
            key={event.id || idx}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="flex items-start gap-3 p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors group"
          >
            <div className="w-8 h-8 rounded-lg bg-brand-cyan/10 border border-brand-cyan/30 flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 text-brand-cyan" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{event.summary}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-slate-400">
                  {formatEventTime(event.start, event.is_all_day)}
                  {!event.is_all_day && ` - ${formatEventTime(event.end, false)}`}
                </span>
                {event.location && (
                  <>
                    <span className="text-slate-600">•</span>
                    <span className="text-xs text-slate-500 truncate">{event.location}</span>
                  </>
                )}
              </div>
            </div>
            {event.html_link && (
              <a
                href={event.html_link}
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-brand-cyan transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </motion.div>
        ))}
      </div>

      {events.length > 10 && (
        <p className="text-xs text-slate-500 mt-2 text-center">
          + {events.length - 10} eventos adicionais
        </p>
      )}
    </motion.div>
  );
};

// Generic Data Card (for any structured data)
export const DataCard = ({ type, data, title }) => {
  switch (type) {
    case 'transactions':
    case 'finance.list':
      return <TransactionsListCard transactions={data.transactions || data} title={title} />;
    case 'finance.summary':
      return <FinanceSummaryCard summary={data} />;
    case 'reminders':
    case 'reminder.list':
      return <RemindersListCard reminders={data.reminders || data} title={title} />;
    case 'tasks':
    case 'task.list':
      return <TasksListCard tasks={data.tasks || data} title={title} />;
    case 'web.search':
      return <WebSearchCard results={data.results} query={data.query} title={title} />;
    case 'calendar.list_today':
    case 'calendar.list_events':
    case 'calendar_events':
      return <CalendarEventsCard events={data.events || data} date={data.date} title={title} />;
    default:
      return null;
  }
};

export default DataCard;
